/**
 * Adjusts the widths of columns to ensure no column width is narrower than its minimum. Distributes
 * remaining space proportionally among unconstrained columns. If a column's proportional share would
 * fall below its minimum it is also fixed at that minimum and space is redistributed iteratively.
 * Throws if the minimums cannot all be satisfied within totalWidth.
 */
export function adjustColumnWidths(widths: number[], totalWidth: number, minWidths: number[]): number[] {
  // Seed fixed set with columns whose natural (percentage-based) width is already below their minimum.
  const fixed = new Set<number>(widths.flatMap((num, index) => (num < minWidths[index] ? [index] : [])));

  for (;;) {
    const fixedWidthSum = [...fixed].reduce((sum, index) => sum + minWidths[index], 0);
    const remaining = totalWidth - fixedWidthSum;

    if (remaining < 0) {
      throw new Error('Cannot adjust numbers to meet the constraints');
    }

    const flexibleIndices = widths.map((_, index) => index).filter((index) => !fixed.has(index));

    if (flexibleIndices.length === 0) {
      // All columns are at their minimums; distribute any leftover space proportionally.
      const totalOriginal = widths.reduce((sum, width) => sum + width, 0);
      return widths.map((width, index) => minWidths[index] + (width / totalOriginal) * remaining);
    }

    const flexibleTotal = flexibleIndices.reduce((sum, index) => sum + widths[index], 0);

    // Promote any flexible column whose proportional share would fall below its minimum.
    const underwhelmed = flexibleIndices.filter((index) => (widths[index] / flexibleTotal) * remaining < minWidths[index]);

    if (underwhelmed.length === 0) {
      return widths.map((_, index) => {
        if (fixed.has(index)) return minWidths[index];
        return (widths[index] / flexibleTotal) * remaining;
      });
    }

    underwhelmed.forEach((index) => fixed.add(index));
  }
}
